import React, { useState, useEffect } from 'react';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../orderStatus';
import { api } from '../api';
import { formatPhoneDisplay } from '../utils';
import '../styles/WorkloadPage.css';
import { IconFlorist, IconPacker, IconCourier } from '../components/StatIcons';

// Плашки статусов — общая палитра заказа (orderStatus.js), как в карточке и списке (UX-аудит 25.10)
const statusBadgeStyles = ORDER_STATUS_COLORS;

const statusLabels = ORDER_STATUS_LABELS; // единые подписи (src/orderStatus.js)

function WorkloadPage({ showToast }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('florist');
  const [workloadData, setWorkloadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedAssigned, setExpandedAssigned] = useState(new Set());
  const [expandedCompleted, setExpandedCompleted] = useState(new Set());

  useEffect(() => {
    loadWorkloadData();
  }, [selectedDate]);

  const loadWorkloadData = async () => {
    setLoading(true);
    try {
      // Форматируем дату в локальном часовом поясе (не UTC)
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      const data = await api.getWorkloadStats(dateStr);
      setWorkloadData(data);
    } catch (error) {
      console.error('Failed to load workload data', error);
      showToast?.('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  };
  const goToNext = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
  };
  const goToToday = () => setSelectedDate(new Date());

  const formatDate = (date) =>
    date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'short' });

  const toggleAssigned = (employeeId) => {
    setExpandedAssigned(prev => {
      const s = new Set(prev);
      s.has(employeeId) ? s.delete(employeeId) : s.add(employeeId);
      return s;
    });
  };

  const toggleCompleted = (employeeId) => {
    setExpandedCompleted(prev => {
      const s = new Set(prev);
      s.has(employeeId) ? s.delete(employeeId) : s.add(employeeId);
      return s;
    });
  };

  // ========== Compact order card renderers per role ==========

  const itemsSummary = (items) => {
    if (!items || items.length === 0) return '';
    const parts = items.slice(0, 2).map(it => `${it.product_name} x${it.quantity}`);
    if (items.length > 2) parts.push(`+${items.length - 2}`);
    return parts.join(', ');
  };

  const renderFloristCard = (order) => (
    <div key={order.id} className="wl-row">
      <span className="wl-row-id">#{order.order_number}</span>
      <span className="wl-status-badge" style={{ background: statusBadgeStyles[order.status]?.bg, color: statusBadgeStyles[order.status]?.color }}>
        {statusLabels[order.status]}
      </span>
      <span className="wl-row-time">{order.delivery_time || '---'}</span>
      <span className="wl-row-items">{itemsSummary(order.items)}</span>
      <span className="wl-row-pack">{order.packaging_type === 'gift' ? '🎁' : '📦'}</span>
      {order.packer && <span className="wl-row-assign" style={{ color: '#BD8E00' }}>📦 {order.packer.name}</span>}
      {order.odd_quantity && <span className="wl-row-odd">Нечёт</span>}
      {order.admin_comment && <span className="wl-row-warn" title={order.admin_comment}>⚠️</span>}
      {order.greeting && <span className="wl-row-icon" title={order.greeting}>💌</span>}
      {order.courier_comment && <span className="wl-row-icon" title={order.courier_comment}>💬</span>}
    </div>
  );

  const renderPackerCard = (order) => (
    <div key={order.id} className="wl-row">
      <span className="wl-row-id">#{order.order_number}</span>
      <span className="wl-status-badge" style={{ background: statusBadgeStyles[order.status]?.bg, color: statusBadgeStyles[order.status]?.color }}>
        {statusLabels[order.status]}
      </span>
      <span className="wl-row-time">{order.delivery_time || '---'}</span>
      <span className="wl-row-items">{itemsSummary(order.items)}</span>
      <span className="wl-row-pack">{order.packaging_type === 'gift' ? '🎁' : '📦'}</span>
      {order.florist && <span className="wl-row-assign" style={{ color: '#1F763B' }}>🌸 {order.florist.name}</span>}
      {order.odd_quantity && <span className="wl-row-odd">Нечёт</span>}
      {order.admin_comment && <span className="wl-row-warn" title={order.admin_comment}>⚠️</span>}
      {order.greeting && <span className="wl-row-icon" title={order.greeting}>💌</span>}
      {order.courier_comment && <span className="wl-row-icon" title={order.courier_comment}>💬</span>}
    </div>
  );

  const renderCourierCard = (order) => {
    const contactName = order.is_recipient_other
      ? order.recipient_name
      : `${order.user?.first_name || 'Клиент'} ${order.user?.last_name || ''}`.trim();
    const contactPhone = order.is_recipient_other ? order.recipient_phone : order.phone;

    const addrParts = [order.delivery_address || 'Самовывоз'];
    if (order.apartment) addrParts.push(`кв. ${order.apartment}`);
    if (order.entrance) addrParts.push(`п. ${order.entrance}`);
    if (order.floor) addrParts.push(`эт. ${order.floor}`);
    if (order.intercom) addrParts.push(`дом. ${order.intercom}`);

    return (
      <div key={order.id} className="wl-row">
        <span className="wl-row-id">#{order.order_number}</span>
        <span className="wl-status-badge" style={{ background: statusBadgeStyles[order.status]?.bg, color: statusBadgeStyles[order.status]?.color }}>
          {statusLabels[order.status]}
        </span>
        <span className="wl-row-time">{order.delivery_time || '---'}</span>
        <span className="wl-row-contact">
          {order.is_recipient_other ? '📨' : '👤'} {contactName}
          <span className="wl-row-phone">📞 {formatPhoneDisplay(contactPhone)}</span>
        </span>
        <span className="wl-row-addr">
          {order.city_district && <span className="wl-row-district">📍{order.city_district}</span>}
          {addrParts.join(', ')}
        </span>
        {order.admin_comment && <span className="wl-row-warn" title={order.admin_comment}>⚠️</span>}
        {order.courier_comment && <span className="wl-row-icon" title={order.courier_comment}>💬</span>}
      </div>
    );
  };

  const renderCard = (order) => {
    if (activeTab === 'florist') return renderFloristCard(order);
    if (activeTab === 'packer') return renderPackerCard(order);
    return renderCourierCard(order);
  };

  // ========== Main render ==========

  const currentSummary = workloadData?.summary || {};
  const currentEmployees = workloadData
    ? (activeTab === 'florist' ? workloadData.florists :
       activeTab === 'packer' ? workloadData.packers :
       workloadData.couriers)
    : [];

  if (loading && !workloadData) {
    return <div className="workload-page"><div className="wl-loading">Загрузка...</div></div>;
  }

  return (
    <div className="workload-page">
      {/* Заголовок убран — показывается во вкладке AnalyticsPage */}

      <div className="wl-date-nav">
        <button className="btn btn-sm btn-secondary" onClick={goToPrevious}>←</button>
        <button className="btn btn-sm btn-secondary" onClick={goToToday}>Сегодня</button>
        <button className="btn btn-sm btn-secondary" onClick={goToNext}>→</button>
        <div className="wl-date-label">{formatDate(selectedDate)}</div>
      </div>

      {/* Summary cards as clickable tabs */}
      <div className="wl-summary-tabs">
        <button
          className={`wl-summary-tab florist ${activeTab === 'florist' ? 'active' : ''}`}
          onClick={() => setActiveTab('florist')}
        >
          <span className="stat-icon wl-summary-icon"><IconFlorist /></span>
          <span className="wl-summary-label">Флористы</span>
          <span className="wl-summary-count">{currentSummary.florists_active || 0} в работе</span>
        </button>
        <button
          className={`wl-summary-tab packer ${activeTab === 'packer' ? 'active' : ''}`}
          onClick={() => setActiveTab('packer')}
        >
          <span className="stat-icon wl-summary-icon"><IconPacker /></span>
          <span className="wl-summary-label">Упаковщики</span>
          <span className="wl-summary-count">{currentSummary.packers_active || 0} в работе</span>
        </button>
        <button
          className={`wl-summary-tab courier ${activeTab === 'courier' ? 'active' : ''}`}
          onClick={() => setActiveTab('courier')}
        >
          <span className="stat-icon wl-summary-icon"><IconCourier /></span>
          <span className="wl-summary-label">Курьеры</span>
          <span className="wl-summary-count">{currentSummary.couriers_active || 0} в работе</span>
        </button>
      </div>

      {/* Employees with orders */}
      <div className="wl-employees">
        {currentEmployees.length === 0 ? (
          <div className="wl-empty">Нет сотрудников</div>
        ) : (
          currentEmployees.map(employee => (
            <div key={employee.id} className="wl-employee-block">
              <div className="wl-employee-header">
                <span className="wl-employee-name">{employee.name}</span>
                {employee.assigned_count > 0 && (
                  <span className="wl-badge assigned">Назначено: {employee.assigned_count}</span>
                )}
                <span className="wl-badge active">В работе: {employee.active_count}</span>
                <span className="wl-badge completed">Выполнено: {employee.completed_count}</span>
              </div>

              {/* Assigned orders — collapsible (only for packers) */}
              {employee.assigned_orders && employee.assigned_orders.length > 0 && (
                <div className="wl-assigned-section">
                  <button
                    className="wl-assigned-toggle"
                    onClick={() => toggleAssigned(employee.id)}
                  >
                    {expandedAssigned.has(employee.id) ? '▼' : '▶'} Назначенные ({employee.assigned_count})
                  </button>
                  {expandedAssigned.has(employee.id) && (
                    <div className="wl-orders-list assigned">
                      {employee.assigned_orders.map(order => renderCard(order))}
                    </div>
                  )}
                </div>
              )}

              {/* Active orders — always visible */}
              {employee.active_orders.length > 0 && (
                <div className="wl-orders-list">
                  {employee.active_orders.map(order => renderCard(order))}
                </div>
              )}

              {employee.active_orders.length === 0 && employee.completed_orders.length === 0 && (!employee.assigned_orders || employee.assigned_orders.length === 0) && (
                <div className="wl-no-orders">Нет заказов на выбранную дату</div>
              )}

              {/* Completed orders — collapsible */}
              {employee.completed_orders.length > 0 && (
                <div className="wl-completed-section">
                  <button
                    className="wl-completed-toggle"
                    onClick={() => toggleCompleted(employee.id)}
                  >
                    {expandedCompleted.has(employee.id) ? '▼' : '▶'} Выполненные ({employee.completed_count})
                  </button>
                  {expandedCompleted.has(employee.id) && (
                    <div className="wl-orders-list completed">
                      {employee.completed_orders.map(order => renderCard(order))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default WorkloadPage;
